import React from "react"

import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid"
import { ClockIcon } from "@heroicons/react/24/outline"

import { KitInventoryTable, KitItem } from "./KitInventoryTable"
import { VariantInventoryItem, VariantInventoryTable } from "./VariantInventoryTable"
import { Bee } from "~/src/components/BeeKit"
import { Image } from "~/src/components/Image"
import { StockIndicator } from "~/src/components/StockIndicator"

export type StoreItemInventoryTileProps = {
  /**
   * Unique ID of the item.
   */
  id?: string
  /**
   * Title (i.e., `display_name`) of the item.
   */
  title: string
  /**
   * HREF to the store page for this item.
   */
  storeItemHref?: string
  /**
   * Indicates if the item is an electronic gift card.
   */
  eGift?: boolean
  /**
   * Indicates if the item is fulfilled on demand.
   */
  onDemand?: boolean
  /**
   * Indicates if the item is hidden from the store.
   */
  hiddenFromStore?: boolean
  /**
   * The rank of the seller for this item, used for sorting or prioritization.
   */
  sellerRank?: number
  /**
   * Source URL for the thumbnail image.
   */
  thumbnailSrc?: string
  /**
   * Amount of items in stock. Set to `Infinity` if the item is always in stock (i.e., not required).
   */
  quantity?: number
  /**
   * HREF to the order history of this item. Should be HREF to history of all variants when `variantItems` is used.
   */
  historyHref?: string

  /**
   * HREF to the reorder popup modal of this item.
   */
  reorderHref?: string

  /**
   * Amount awaiting in-stock notifications for the store product
   */
  waitlistQuantity?: number
}

export type LinkedKit = {
  title: string
  href: string
}

export type Children = {
  linkedKits?: LinkedKit[]
  children?: React.ReactNode
}

export type VariantItemsProps = {
  linkedKits?: LinkedKit[]
  variantItems: VariantInventoryItem[]
}

export type KitItemsProps = {
  kitItems: KitItem[]
}

export function StoreItemInventoryTile(
  props: StoreItemInventoryTileProps & (Children | VariantItemsProps | KitItemsProps)
): JSX.Element {
  const {
    title,
    storeItemHref,
    thumbnailSrc,
    quantity,
    historyHref,
    reorderHref,
    eGift,
    onDemand,
    hiddenFromStore,
    sellerRank,
    waitlistQuantity,
  } = props

  let children: React.ReactNode
  if ("variantItems" in props) {
    children = <VariantInventoryTable items={props.variantItems} />
  } else if ("kitItems" in props) {
    children = <KitInventoryTable items={props.kitItems} />
  } else if ("children" in props) {
    children = props.children
  }

  let linkedKits: LinkedKit[] | undefined
  if ("linkedKits" in props) {
    linkedKits = props.linkedKits
  }

  const badges = [
    sellerRank != null && sellerRank <= 5 && sellerRank > 0 && (
      <Bee.Pill key="sellerRank" className="bg-sky-100">
        #{sellerRank} seller in store
      </Bee.Pill>
    ),
    eGift && (
      <Bee.Pill key="eGift" className="bg-orange-100">
        eGift
      </Bee.Pill>
    ),
    onDemand && (
      <Bee.Pill key="onDemand" className="bg-green-100">
        On Demand
      </Bee.Pill>
    ),
    hiddenFromStore && (
      <Bee.Pill key="hiddenFromStore" className="bg-gray-200">
        Hidden from store
      </Bee.Pill>
    ),
  ].filter((x) => x != null && x !== false)

  return (
    <div className="flex gap-6">
      <section className="shrink-0">
        <Image className="h-[9.25rem] w-[9.25rem] rounded-lg" src={thumbnailSrc} />
      </section>

      <section className="grow">
        <hgroup className="mb-3">
          <div className="flex items-center justify-start gap-2">
            <h1 className="text-lg font-medium">{title}</h1>
            {storeItemHref && (
              <a className="content" href={storeItemHref} target="_blank" rel="noopener noreferrer">
                <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />
              </a>
            )}
            {quantity != null && <StockIndicator className="ml-auto" quantity={quantity} />}
          </div>
          {badges.length > 0 && <div className="flex gap-2">{badges}</div>}
        </hgroup>

        <main>
          {historyHref && !("variantItems" in props) && (
            <a className="my-3 block text-sm text-gray-500 underline" href={historyHref}>
              Order history
            </a>
          )}

          {waitlistQuantity && !("variantItems" in props) && (
            <span className="flex items-center gap-1 text-sm text-gray-500">
              <ClockIcon className="inline h-4 w-4 text-sky-500" />
              <strong className="font-medium">{waitlistQuantity} on waitlist</strong>
            </span>
          )}

          {linkedKits && linkedKits.length > 0 && (
            <div className="my-3 flex flex-col flex-wrap text-sm text-gray-500">
              <h1 className="font-medium">Included in {linkedKits.length} kits:</h1>
              <ul className="list-inside list-disc pl-2">
                {linkedKits.map(({ title, href }) => (
                  <li key={href} className="text-sm underline">
                    <a className="-m-2 inline-block" href={href} target="_blank" rel="noopener noreferrer">
                      {title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {children}

          {historyHref && "variantItems" in props && (
            <a className="mt-3 block text-sm text-gray-500 underline" href={historyHref}>
              See order history for all variants
            </a>
          )}
        </main>
      </section>
    </div>
  )
}
